import React, { memo, useEffect, useState, Component } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import "core-js/es6";
import "antd/dist/antd.css";
// Linchpin design-system tokens (CSS vars on :root, inert until a `.lp-v3`
// scope opts in) + base layer. Loaded once at app entry so any v2 redesign
// surface (e.g. the new Items & Measure section) can use them without extra
// per-screen wiring. See app/frontend/src/stylesheets/linchpin/base.css.
import "@fleetenable/linchpin-tokens/dist/tokens.css";
import "../stylesheets/linchpin/base.css";
import App, { FleetLayout } from './FleetLayout.jsx';
import ProtectedRoute from './ProtectedRoute';
import AuthForm from './components/Auth/AuthForm';
import SamlCallbackPage from './components/Auth/SamlCallbackPage';
import { isTokenValid } from './api/ApiUtils.js';
import AppContextProvider from './context/AppContextProvider';
import { AppLoadingScreen } from './components/common/LogisticsLoadingScreen';
import AppConfig from './config/AppConfig';
import { normalizeAcademyReturnPath } from './helpers/common';

// Navigation handler component - uses withRouter to access history (React Router v5)
class DrayageNavigationHandlerClass extends Component {
  componentDidMount() {
    this.handleDrayageNavigation = (event) => {
      const { type, data } = event.detail || {};

      if (type === 'NAVIGATE_TO_ROUTE') {
        const { path } = data || {};
        if (path) {
          this.props.history.push(path);
        }
      }
    };

    window.addEventListener("drayage-to-fleet", this.handleDrayageNavigation);
  }

  componentWillUnmount() {
    window.removeEventListener("drayage-to-fleet", this.handleDrayageNavigation);
  }

  render() {
    return null;
  }
}

const DrayageNavigationHandler = withRouter(DrayageNavigationHandlerClass);

// Linehaul navigation handler - listens for cross-app navigation from Linehaul UI
class LinehaulNavigationHandlerClass extends Component {
  componentDidMount() {
    this.handleLinehaulNavigation = (event) => {
      const { type, data } = event.detail || {};

      if (type === 'NAVIGATE_TO_ORDER') {
        const { orderId, routeId, routeType, origin } = data || {};
        
        if (orderId) {
          // Set context if we have routeId OR if this came from linehaul (origin check)
          if (routeId || origin === 'linehaul') {
            const contextData = { routeId, routeType, fromLinehaul: true };
            sessionStorage.setItem('linehaulBackContext', JSON.stringify(contextData));
            // Set flag so OrderConfigurations knows we came from linehaul
            sessionStorage.setItem('justCameFromLinehaul', 'true');
          }
          this.props.history.push(`/orders/${orderId}/details`);
        }
      }
    };

    window.addEventListener("linehaul-to-fleet", this.handleLinehaulNavigation);
  }

  componentWillUnmount() {
    window.removeEventListener("linehaul-to-fleet", this.handleLinehaulNavigation);
  }

  render() {
    return null;
  }
}

const LinehaulNavigationHandler = withRouter(LinehaulNavigationHandlerClass);

/** Already logged into Fleet; resume Academy via token exchange (session-expired re-login flow). */
function AcademySessionResume({ returnPath }) {
  useEffect(() => {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    const token = auth.access_token;
    const userId = auth.user_id;
    if (!token || !userId) return;
    const base = String(AppConfig.academyUrl || "").replace(/\/$/, "");
    sessionStorage.setItem("academy_return_path", returnPath);
    const url = `${base}/auth/validate?token=${encodeURIComponent(token)}&resource_owner_id=${encodeURIComponent(userId)}&redirect=${encodeURIComponent(returnPath)}`;
    window.location.replace(url);
  }, [returnPath]);
  return <AppLoadingScreen />;
}


/*
* Proxy for localStorage events to handle changes in localStorage
* This is useful for components / modules that need to react to localStorage changes
* from the current page or other pages in the same tab.
*/

const originalSetItem = localStorage.setItem;
const originalRemoveItem = localStorage.removeItem;

localStorage.setItem = function (key, value) {
  originalSetItem.apply(this, arguments);
  window.dispatchEvent(new CustomEvent('localStorageChange', {
    detail: { key, value, type: 'set' }
  }));
};

localStorage.removeItem = function (key) {
  originalRemoveItem.apply(this, arguments);
  window.dispatchEvent(new CustomEvent('localStorageChange', {
    detail: { key, type: 'remove' }
  }));
};

const FleetEnableApp = (props) => {
  const [ isLoading, setIsLoading ] = useState(true);

  const isUserAuthenticated = () => {
    const token = localStorage.getItem("auth") ? JSON.parse(localStorage.getItem("auth")) : null;
    if (token) {
      const isTokenValidResult = isTokenValid(token);
      if (isTokenValidResult) {
        return true;
      }
    }
    localStorage.clear();
    return false;
  };

  useEffect(() => {
    setIsLoading(false);

    const navigateToOrderDetailsFromLinehaul = (event) => {
      const { data } = event;
      
      if (data && typeof data === 'object' && data.type === 'NAVIGATE_TO_ORDER') {
        const { orderId, routeId, routeType, origin } = data;

        // Set context if we have routeId OR if this came from linehaul
        if (routeId || origin === 'linehaul') {
          const contextData = { routeId, routeType, fromLinehaul: true };
          sessionStorage.setItem('linehaulBackContext', JSON.stringify(contextData));
          // Set flag so OrderConfigurations knows we came from linehaul
          sessionStorage.setItem('justCameFromLinehaul', 'true');
        }
        // Navigate using your routing or full page redirect
        window.location.href = `/orders/${orderId}/details`;
      }
    };

    window.addEventListener("message", navigateToOrderDetailsFromLinehaul);

    return () => {
      window.removeEventListener("message", navigateToOrderDetailsFromLinehaul);
    };
  }, []);

  const url = new URL(window.location.href);
  const searchParams = url.pathname === "/forgot_password" ? new URLSearchParams(url.search) : new URLSearchParams("");
  const reset_password_token = searchParams.get('reset_password_token');
  const loginParams = new URLSearchParams(url.pathname === "/login" ? url.search : "");
  const academyReturnPath = normalizeAcademyReturnPath(loginParams.get("return_to"));

  return (
    <Router>
      <>
        <DrayageNavigationHandler />
        <LinehaulNavigationHandler />
        <Switch>
          <Route
            path="/login"
            render={() => {
              if (isLoading) {
                return <AppLoadingScreen />;
              }
              if (isUserAuthenticated() && academyReturnPath) {
                return <AcademySessionResume returnPath={academyReturnPath} />;
              }
              // Reset password ONLY for unauthenticated users
              if (!isUserAuthenticated() && reset_password_token) {
                return <AuthForm resetPasswordToken={reset_password_token} />;
              }
              if (!isUserAuthenticated()) {
                return <AuthForm />;
              }
              // ✅ Authenticated users always go to dashboard
              return <Redirect to="/" />;
            }}
          />

          {/*
            Okta SAML redirect lands here without a Fleet token. Must not use ProtectedRoute
            (path "/" is exact — /auth/saml/callback would not match and would fall through Switch).
          */}
          <Route exact path="/auth/saml/callback" component={SamlCallbackPage} />

          <ProtectedRoute
            isAuthenticated={isUserAuthenticated}
            path="/"
            component={() => (
              !isLoading ? (
                <AppContextProvider>
                  <DndProvider backend={ HTML5Backend }>
                    <App />
                  </DndProvider>
                </AppContextProvider>
              ) : (
                <AppLoadingScreen />
              )
            )}
          />
        </Switch>
      </>
    </Router>
  );
};

export default FleetEnableApp;
